/**
 * usePagination Hook
 * 페이지네이션 로직을 재사용하기 위한 커스텀 훅
 */
import { useState, useMemo } from 'react';

interface UsePaginationProps<T> {
    items: T[];
    itemsPerPage?: number;
}

interface UsePaginationReturn<T> {
    currentPage: number;
    totalPages: number;
    currentItems: T[];
    setCurrentPage: (page: number) => void;
    goToPage: (page: number) => void;
    goToFirstPage: () => void;
    goToLastPage: () => void;
    goToNextPage: () => void;
    goToPreviousPage: () => void;
    resetPagination: () => void;
}

export const usePagination = <T,>({
    items,
    itemsPerPage = 15
}: UsePaginationProps<T>): UsePaginationReturn<T> => {
    const [currentPage, setCurrentPage] = useState(1);

    // 전체 페이지 수 계산
    const totalPages = useMemo(() => {
        return Math.ceil(items.length / itemsPerPage);
    }, [items.length, itemsPerPage]);

    // 현재 페이지의 아이템들
    const currentItems = useMemo(() => {
        const startIndex = (currentPage - 1) * itemsPerPage;
        const endIndex = startIndex + itemsPerPage;
        return items.slice(startIndex, endIndex);
    }, [items, currentPage, itemsPerPage]);

    // 페이지 변경
    const goToPage = (page: number) => {
        const validPage = Math.max(1, Math.min(page, totalPages));
        setCurrentPage(validPage);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // 첫 페이지로
    const goToFirstPage = () => goToPage(1);

    // 마지막 페이지로
    const goToLastPage = () => goToPage(totalPages);

    // 다음 페이지로
    const goToNextPage = () => goToPage(currentPage + 1);

    // 이전 페이지로
    const goToPreviousPage = () => goToPage(currentPage - 1);

    // 페이지네이션 초기화 (검색 후 사용)
    const resetPagination = () => setCurrentPage(1);

    return {
        currentPage,
        totalPages,
        currentItems,
        setCurrentPage,
        goToPage,
        goToFirstPage,
        goToLastPage,
        goToNextPage,
        goToPreviousPage,
        resetPagination
    };
};
